import React from 'react';
import styled from 'styled-components';
import { siteConfig } from '../siteConfig';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const LAST_UPDATED = '28 August 2026';
const MANAGE_URL = 'https://www.amazon.co.uk/ap/adam';
const ICO_URL = 'https://ico.org.uk/make-a-complaint/';

const Page = styled.main`
  padding: calc(${({ theme }) => theme.navHeight} + clamp(48px, 9vw, 104px))
    ${({ theme }) => theme.container.gutter} clamp(64px, 10vw, 120px);
`;

const Inner = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.74rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent};
`;

const Title = styled.h1`
  max-width: 16ch;
  margin-top: 18px;
  font-size: clamp(2.2rem, 6vw, 3.5rem);
  line-height: 1.04;
  letter-spacing: -0.025em;
  font-weight: 600;
`;

const Updated = styled.p`
  margin-top: 20px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.76rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.muted};
`;

const Lede = styled.p`
  margin-top: 28px;
  font-size: 1.12rem;
  line-height: 1.65;
`;

const Rule = styled.hr`
  margin: clamp(40px, 6vw, 64px) 0 0;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
`;

const Section = styled.section`
  margin-top: clamp(36px, 5vw, 52px);
`;

const Heading = styled.h2`
  display: flex;
  gap: 14px;
  align-items: baseline;
  font-size: clamp(1.2rem, 2.6vw, 1.55rem);
  letter-spacing: -0.015em;
  font-weight: 600;
`;

const Num = styled.span`
  flex: none;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const Body = styled.div`
  margin-top: 14px;
  padding-left: calc(0.8rem + 14px);

  p + p,
  p + ul,
  ul + p {
    margin-top: 14px;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li + li {
    margin-top: 8px;
  }

  strong {
    font-weight: 600;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-left: 0;
  }
`;

const Callout = styled.div`
  margin-top: 18px;
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-left: 3px solid ${({ theme }) => theme.colors.accent};
  border-radius: 6px;
`;

interface Clause {
  id: string;
  heading: string;
  body: React.ReactNode;
}

const clauses: Clause[] = [
  {
    id: 'who-we-are',
    heading: 'Who we are',
    body: (
      <>
        <p>
          {siteConfig.companyName} is a technology consultancy registered in England and Wales
          (company number {siteConfig.companyNumber}) and based in {siteConfig.location}. We are
          the data controller for the personal data described in this notice.
        </p>
        <p>
          You can reach us about anything on this page at{' '}
          <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.
        </p>
      </>
    )
  },
  {
    id: 'scope',
    heading: 'What this notice covers',
    body: (
      <>
        <p>
          This notice explains what happens when you choose <strong>Login with Amazon</strong> to
          sign in to an application operated by {siteConfig.companyName}, and it is the notice
          linked from the Amazon consent screen you see before you authorise us.
        </p>
        <p>
          It covers the Amazon sign-in itself. An individual application may have its own, fuller
          privacy policy covering everything else it does with your data; where one exists, that
          policy applies alongside this notice.
        </p>
      </>
    )
  },
  {
    id: 'what-we-receive',
    heading: 'What we receive from Amazon',
    body: (
      <>
        <p>
          Amazon shows you exactly what you are agreeing to share on the consent screen. We only
          receive the data covered by the permissions (scopes) you approve there:
        </p>
        <ul>
          <li>
            <strong>Your Amazon user ID</strong> — an opaque identifier Amazon issues for you. We
            always receive this; it is what lets us recognise you when you return.
          </li>
          <li>
            <strong>Your name and email address</strong> — where the application requests Amazon's
            profile permission, which most sign-in flows do.
          </li>
          <li>
            <strong>Data covered by any additional permission you approve</strong> — some
            applications request service-specific scopes, such as those for an Alexa skill or the
            Amazon Selling Partner API. We receive only what those named permissions cover, and
            only if you approve them.
          </li>
          <li>
            <strong>Access and refresh tokens</strong> — short-lived credentials Amazon issues so
            the application can act on the permissions you granted.
          </li>
        </ul>
        <Callout>
          <p>
            We never receive your Amazon password. We do not receive your payment details, your
            order history, or anything else held in your Amazon account unless it falls within a
            permission you explicitly approved.
          </p>
        </Callout>
      </>
    )
  },
  {
    id: 'how-we-use-it',
    heading: 'How we use it',
    body: (
      <>
        <p>We use the data we receive from Amazon to:</p>
        <ul>
          <li>sign you in and keep you signed in;</li>
          <li>
            create and maintain your account, including matching your Amazon sign-in to an existing
            account with the same email address;
          </li>
          <li>contact you about the service — service notices, security alerts and support;</li>
          <li>
            operate the specific features you authorised, where you approved a service-specific
            permission;
          </li>
          <li>keep the service secure, and detect and prevent fraud and abuse.</li>
        </ul>
        <p>
          We do not use it for advertising, and we do not build marketing profiles from it.
        </p>
      </>
    )
  },
  {
    id: 'legal-basis',
    heading: 'Our legal basis',
    body: (
      <>
        <p>Under the UK GDPR we rely on:</p>
        <ul>
          <li>
            <strong>Performance of a contract</strong> (Article 6(1)(b)) — to sign you in and
            provide the service you asked for;
          </li>
          <li>
            <strong>Legitimate interests</strong> (Article 6(1)(f)) — to keep our services secure
            and to prevent fraud and abuse;
          </li>
          <li>
            <strong>Consent</strong> (Article 6(1)(a)) — the authorisation you give on the Amazon
            consent screen, which you can withdraw at any time.
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'sharing',
    heading: 'Who we share it with',
    body: (
      <>
        <p>
          We do not sell, rent or trade your personal data, and we do not share it with advertisers
          or data brokers.
        </p>
        <p>We share it only with:</p>
        <ul>
          <li>
            <strong>Service providers acting on our instructions</strong> — hosting, database,
            email delivery and error-monitoring providers, each bound by a written contract that
            limits them to processing the data on our behalf;
          </li>
          <li>
            <strong>Our client, where the application was built for one</strong> — in that case the
            client is the operator of the service you signed in to, and their own privacy policy
            also applies;
          </li>
          <li>
            <strong>Anyone we are legally required to share it with</strong> — for example in
            response to a valid court order or regulatory request.
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'storage',
    heading: 'Where we store it',
    body: (
      <p>
        We host in the United Kingdom or the European Economic Area wherever we can. Where a
        provider processes data outside those regions, we rely on UK adequacy regulations or the
        UK International Data Transfer Agreement (or the Addendum to the EU Standard Contractual
        Clauses) to protect it.
      </p>
    )
  },
  {
    id: 'retention',
    heading: 'How long we keep it',
    body: (
      <>
        <p>
          We keep the data for as long as your account is active. If you revoke our access or ask
          us to delete your account, we delete the Amazon tokens immediately and the associated
          personal data within 30 days.
        </p>
        <p>
          We may keep a minimal record for longer where the law requires it, or where we need it to
          establish, exercise or defend a legal claim.
        </p>
      </>
    )
  },
  {
    id: 'withdrawing-consent',
    heading: 'Withdrawing your consent',
    body: (
      <>
        <p>
          You can revoke our access to your Amazon account at any time from{' '}
          <a href={MANAGE_URL} target="_blank" rel="noopener noreferrer">
            Manage Login with Amazon
          </a>{' '}
          in your Amazon account settings (Your Account &rarr; Manage your data &rarr; Manage apps
          and services with data access). Revoking access stops any further data reaching us.
        </p>
        <p>
          Revoking access does not by itself delete what we already hold. To have that deleted as
          well, email us at <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a> and we
          will action it.
        </p>
      </>
    )
  },
  {
    id: 'your-rights',
    heading: 'Your rights',
    body: (
      <>
        <p>Under the UK GDPR you have the right to:</p>
        <ul>
          <li>ask for a copy of the personal data we hold about you;</li>
          <li>have inaccurate data corrected;</li>
          <li>have your data erased;</li>
          <li>restrict or object to how we process it;</li>
          <li>receive your data in a portable, machine-readable format;</li>
          <li>withdraw your consent at any time.</li>
        </ul>
        <p>
          Email <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a> to exercise any of
          these; we respond within one month. If you are not happy with our response you can
          complain to the Information Commissioner&apos;s Office at{' '}
          <a href={ICO_URL} target="_blank" rel="noopener noreferrer">
            ico.org.uk
          </a>
          .
        </p>
      </>
    )
  },
  {
    id: 'children',
    heading: 'Children',
    body: (
      <p>
        Our services are not directed at children, and we do not knowingly collect data from anyone
        under 16. If you believe a child has signed in to one of our services, contact us and we
        will delete the account.
      </p>
    )
  },
  {
    id: 'this-website',
    heading: 'This website',
    body: (
      <p>
        proteus.tech uses Google Analytics to count visits and understand which pages are read.
        That is separate from Login with Amazon: no data we receive from Amazon is sent to Google
        Analytics, and analytics data is never linked to your Amazon account.
      </p>
    )
  },
  {
    id: 'changes',
    heading: 'Changes to this notice',
    body: (
      <p>
        If we change how we handle data received through Login with Amazon, we will update this
        page and move the date at the top. Material changes affecting existing users will be
        notified by email.
      </p>
    )
  },
  {
    id: 'contact',
    heading: 'Contact us',
    body: (
      <Callout>
        <p>
          {siteConfig.companyName}
          <br />
          {siteConfig.location}
          <br />
          Company number {siteConfig.companyNumber} (England &amp; Wales)
          <br />
          <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>
        </p>
      </Callout>
    )
  }
];

const AmazonPrivacy: React.FC = () => {
  useDocumentTitle('Login with Amazon Consent Privacy Notice | Proteus Technologies');

  return (
    <Page>
      <Inner>
        <Eyebrow>Login with Amazon</Eyebrow>
        <Title>
          Consent <em>Privacy</em> Notice
        </Title>
        <Updated>Last updated: {LAST_UPDATED}</Updated>
        <Lede>
          When you sign in to one of our applications with your Amazon account, Amazon asks you to
          approve what you share with us. This page explains what we receive, what we do with it,
          how long we keep it, and how to take it back.
        </Lede>
        <Rule />

        {clauses.map(({ id, heading, body }, index) => (
          <Section key={id} id={id}>
            <Heading>
              <Num>{String(index + 1).padStart(2, '0')}</Num>
              {heading}
            </Heading>
            <Body>{body}</Body>
          </Section>
        ))}
      </Inner>
    </Page>
  );
};

export default AmazonPrivacy;
